import { type User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { useCallback, useEffect } from 'react';

import { optOutProcess } from '@/features/account';
import {
  logOut,
  mapFirebaseUserToUser,
  selectAuth,
  setUser,
  useGetUserInfoMutation,
} from '@/features/auth';
import { appConfig } from '@/providers/config';
import { auth as firebaseAuth, setDatabase } from '@/providers/firebase';

import { useAppDispatch, useAppSelector } from './store';

export const useAuth = (): {
  syncUserState: (user: FirebaseUser | null) => void;
} => {
  const auth = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();
  const [getUsers] = useGetUserInfoMutation();

  const syncUserState = useCallback(
    (user: FirebaseUser | null): void => {
      if (user !== null && user.emailVerified) {
        const mappedUser = mapFirebaseUserToUser(user);
        if (appConfig.featureFlags.multiDatabase) {
          void user.getIdToken().then((token) => {
            getUsers({ uid: user.uid, token })
              .then((response) => {
                if ('data' in response) {
                  setDatabase(response.data);
                  void optOutProcess(user.uid);
                  dispatch(setUser(mappedUser));
                } else if ('error' in response) {
                  dispatch(logOut());
                }
              })
              .catch(() => {
                dispatch(logOut());
              });
          });
        } else {
          void optOutProcess(user.uid);
          dispatch(setUser(mappedUser));
        }
      } else {
        if (appConfig.featureFlags.multiDatabase) {
          setDatabase(undefined);
        }
        dispatch(logOut());
      }
    },
    [dispatch, getUsers],
  );

  useEffect(() => {
    const handleFocus = (): void => {
      (async () => {
        const emailValidated = firebaseAuth.currentUser?.emailVerified;
        await firebaseAuth.currentUser?.reload();
        if (
          emailValidated === false &&
          firebaseAuth.currentUser?.emailVerified === true
        ) {
          await firebaseAuth.currentUser.getIdToken(true);
          syncUserState(firebaseAuth.currentUser);
        }

        if (firebaseAuth.currentUser === null || auth.user === null) {
          return;
        }

        if (firebaseAuth.currentUser.email !== auth.user.email) {
          syncUserState(firebaseAuth.currentUser);
        }
        // eslint-disable-next-line no-console
      })().catch(console.error);
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [syncUserState, auth.user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, syncUserState);

    return unsubscribe;
  }, [dispatch, syncUserState]);

  return { syncUserState };
};
